import type { BillingPlan } from '../../services/billingApi';

/**
 * Which plan should be selected after a (re)load of the catalog.
 *
 * Lives in its own module — NOT in UpgradeModal.tsx — because that component
 * imports the billing API, which pulls in Firebase, which jsdom cannot load
 * (house rule: RTL leaf components only). Keeping this rule importable
 * without that dependency chain is what makes it testable at all.
 *
 * `current` is the lawyer's existing selection, if any. It WINS over the
 * default whenever that plan is still on offer, because `loadPlans` re-runs
 * on Retry after a failed checkout — and silently reverting to the default
 * there means the lawyer clicks "Continue to payment" believing they are
 * buying what is on screen and is charged for a different SKU. The returned
 * object is always the FRESH one from `plans` (never the stale `current`),
 * so a price or availability change between attempts is honoured rather
 * than paid at the old price.
 */
export function planToSelect(
  plans: BillingPlan[],
  current: BillingPlan | null,
): BillingPlan | null {
  const stillOffered = current
    ? plans.find((p) => p.plan_code === current.plan_code)
    : undefined;
  if (stillOffered) return stillOffered;
  // Annual pre-selected — prefer the 'pro' tier annual plan if present,
  // else the first annual plan, else just the first plan returned.
  const annualPro = plans.find((p) => p.billing_period === 'annual' && p.tier === 'pro');
  const anyAnnual = plans.find((p) => p.billing_period === 'annual');
  return annualPro ?? anyAnnual ?? plans[0] ?? null;
}

export default planToSelect;
